import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import TariffBreakdownPopover from "@/pages/COMMON/product-catalogue/components/tariff-breakdown-popover";
import type {
  AdditionalTariff,
  ProductBreakdownLine,
  LineType,
} from "@/services/product-catalogue";

import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  useGetTariffEngineeringProductsQuery,
  useRunTariffEngineeringResearchForProductMutation,
} from "@/services/tariff-engineering";
import type {
  TariffEngineeringProduct,
  TariffEngineeringStatus,
} from "@/types/tariff-engineering";
import {
  tableCompactSx,
  tableContainerSx,
  paginationSx,
} from "@/components/table/table-styles";
import { tokens } from "@/tokens";
import { HTSCodeWithTooltip } from "@/components/hts-code-with-tooltip";

const STATUS_STYLES: Record<
  TariffEngineeringStatus,
  { label: string; color: string; bg: string }
> = {
  NOT_RUN: { label: "Not run", color: tokens.text.muted, bg: tokens.bg.hover },
  QUEUED: {
    label: "Queued",
    color: tokens.brand.primary,
    bg: `${tokens.brand.primary}14`,
  },
  RUNNING: {
    label: "Running",
    color: tokens.status.warning,
    bg: `${tokens.status.warning}14`,
  },
  DONE: {
    label: "Done",
    color: tokens.status.success,
    bg: `${tokens.status.success}14`,
  },
  FAILED: {
    label: "Failed",
    color: tokens.status.danger,
    bg: `${tokens.status.danger}14`,
  },
};

const fmtRelativeDate = (s: string | null): string => {
  if (!s) return "—";
  const d = new Date(s);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return d.toLocaleDateString();
};

const StatusChip: FC<{ status: TariffEngineeringStatus }> = ({ status }) => {
  const s = STATUS_STYLES[status];
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        height: 20,
        fontSize: tokens.fontSize["2xs"],
        fontWeight: tokens.fontWeight.semibold,
        color: s.color,
        backgroundColor: s.bg,
        border: "none",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const topSavings = (p: TariffEngineeringProduct): number | null => {
  const ops = p.latestAnalysis?.opportunities;
  if (!ops || ops.length === 0) return null;
  return Math.max(...ops.map((o) => o.savingsPct));
};

// Coerce the GraphQL string lineType back to the LineType enum the
// shared popover expects.
const asLineType = (s: string): LineType => s as LineType;

export const ProductHubTable: FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const scopeArgs = useMemo(() => {
    const args: { tenantId?: string; clientId?: string } = {};
    const isAdmin = user?.userRole === roles.TDADMIN;
    const isTDAdminTCD =
      isAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
    if (selectedTenant?._id) args.tenantId = selectedTenant._id;
    if (selectedClient?._id) {
      args.clientId = selectedClient._id;
    } else if (isTDAdminTCD && selectedTenant?._id) {
      args.clientId = selectedTenant._id;
    }
    return args;
  }, [user, selectedTenant?._id, selectedTenant?.tenantType, selectedClient?._id]);

  const queryArgs = useMemo(
    () => ({ ...scopeArgs, pageNo, pageSize }),
    [scopeArgs, pageNo, pageSize]
  );

  const { data, isFetching, refetch } = useGetTariffEngineeringProductsQuery(
    queryArgs
  );

  const rows = data?.data || [];
  const hasPendingRuns = useMemo(
    () =>
      rows.some(
        (r) => r.analysisStatus === "QUEUED" || r.analysisStatus === "RUNNING"
      ),
    [rows]
  );
  useGetTariffEngineeringProductsQuery(queryArgs, {
    pollingInterval: hasPendingRuns ? 5000 : 0,
    skip: !hasPendingRuns,
  });

  const [runResearch] = useRunTariffEngineeringResearchForProductMutation();
  const [enqueueingIds, setEnqueueingIds] = useState<Set<string>>(
    () => new Set()
  );
  const [popoverProduct, setPopoverProduct] =
    useState<TariffEngineeringProduct | null>(null);

  const handleRun = async (productId: string) => {
    setEnqueueingIds((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
    try {
      await runResearch({ ...scopeArgs, productId }).unwrap();
      refetch();
    } finally {
      setEnqueueingIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const isEmpty = rows.length === 0;

  return (
    <Paper sx={{ ...tableContainerSx, ...tableCompactSx, mt: 2 }}>
      {isFetching && isEmpty ? (
        <Box sx={{ p: 6, textAlign: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 110 }}>SKU</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell sx={{ width: 140 }}>HTS</TableCell>
                  <TableCell sx={{ width: 80 }}>Origin</TableCell>
                  <TableCell align="right" sx={{ width: 130 }}>
                    Today's Duty
                  </TableCell>
                  <TableCell sx={{ width: 90 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>
                    Top Savings
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>Last Run</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>
                    &nbsp;
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((p) => {
                  const top = topSavings(p);
                  const isEnqueueing = enqueueingIds.has(p._id);
                  const isPending =
                    p.analysisStatus === "QUEUED" ||
                    p.analysisStatus === "RUNNING";
                  return (
                    <TableRow
                      hover
                      key={p._id}
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(
                          `/compliance/tariff-engineering/products/${p._id}`
                        )
                      }
                    >
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: tokens.fontSize.xs,
                          color: tokens.text.muted,
                        }}
                      >
                        {p.sku}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: tokens.fontWeight.semibold,
                          maxWidth: 320,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={p.description || p.name}>
                          <span>{p.name}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <HTSCodeWithTooltip htsCode={p.htsCode}>
                          <Typography
                            sx={{
                              fontSize: tokens.fontSize.sm2,
                              fontFamily: "monospace",
                              fontWeight: tokens.fontWeight.medium,
                              color: tokens.text.primary,
                            }}
                          >
                            {p.htsCode}
                          </Typography>
                        </HTSCodeWithTooltip>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 32,
                            height: 22,
                            px: 0.75,
                            borderRadius: tokens.radius.sm,
                            backgroundColor: tokens.bg.hover,
                            color: tokens.text.body,
                            fontSize: tokens.fontSize["2xs"],
                            fontWeight: tokens.fontWeight.semibold,
                            fontFamily: "monospace",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {p.countryOfOrigin}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopoverProduct(p);
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: tokens.fontWeight.semibold,
                            color: tokens.text.primary,
                            cursor: "pointer",
                            textDecoration: "underline dotted",
                            textUnderlineOffset: 3,
                          }}
                        >
                          {(p.totalDutyRate * 100).toFixed(2)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={p.analysisStatus} />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {top !== null ? (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: tokens.fontSize.sm2,
                              fontWeight: tokens.fontWeight.semibold,
                              color: tokens.status.success,
                            }}
                          >
                            −{top.toFixed(1)}pp
                          </Typography>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: tokens.fontSize.xs,
                          color: tokens.text.muted,
                        }}
                      >
                        {fmtRelativeDate(p.latestAnalysis?.finishedAt || null)}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={isEnqueueing || isPending}
                          onClick={() => handleRun(p._id)}
                          sx={{
                            minWidth: 56,
                            height: 26,
                            fontSize: tokens.fontSize.xs,
                            fontWeight: tokens.fontWeight.semibold,
                            borderRadius: tokens.radius.sm,
                            textTransform: "none",
                            py: 0,
                          }}
                        >
                          {isEnqueueing || isPending ? (
                            <CircularProgress size={12} />
                          ) : p.analysisStatus === "DONE" ? (
                            "Re-run"
                          ) : (
                            "Run"
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {isEmpty && !isFetching && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box sx={{ py: 6 }}>
                        {!scopeArgs.tenantId || !scopeArgs.clientId ? (
                          <Typography
                            sx={{
                              fontSize: tokens.fontSize.sm,
                              color: tokens.text.muted,
                            }}
                          >
                            Select a tenant and client to load products.
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: tokens.fontSize.sm,
                              color: tokens.text.muted,
                            }}
                          >
                            No products in the Product Hub for this client yet.
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={paginationSx}>
            <TablePagination
              component="div"
              count={data?.meta.countDocs ?? 0}
              page={pageNo - 1}
              onPageChange={(_, p) => setPageNo(p + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageNo(1);
              }}
            />
          </Box>
        </>
      )}
      <TariffBreakdownPopover
        open={!!popoverProduct}
        onClose={() => setPopoverProduct(null)}
        productName={popoverProduct?.name}
        productSku={popoverProduct?.sku}
        htsCode={popoverProduct?.htsCode}
        countryOfOrigin={popoverProduct?.countryOfOrigin}
        breakdownLines={
          popoverProduct
            ? (popoverProduct.breakdownLines || []).map((l) => ({
                ...l,
                lineType: asLineType(l.lineType),
                applicableRules: l.applicableRules as AdditionalTariff[],
              })) as ProductBreakdownLine[]
            : undefined
        }
        additionalTariffs={
          (popoverProduct?.additionalTariffs as AdditionalTariff[]) ||
          undefined
        }
        totalRate={popoverProduct?.totalDutyRate ?? 0}
        metalPercentage={popoverProduct?.metalPercentage ?? undefined}
      />
    </Paper>
  );
};

export default ProductHubTable;
